/**
 * Notification center Zustand store.
 *
 * Tracks background job notifications (summary generation, re-indexing, etc.).
 * Persisted to localStorage with 24-hour auto-pruning.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface NotificationProgress {
	completed: number;
	total: number;
	failed?: number;
}

export interface Notification {
	id: string;
	type:
		| "summary_generation"
		| "reindex"
		| "content_generation"
		| "publishing";
	title: string;
	message: string;
	status: "running" | "done" | "failed";
	progress?: NotificationProgress;
	createdAt: string;
	read: boolean;
	favorited: boolean;
}

interface NotificationStore {
	notifications: Notification[];
	addNotification: (n: Omit<Notification, "read" | "favorited">) => void;
	updateNotification: (id: string, updates: Partial<Notification>) => void;
	markRead: (id: string) => void;
	markAllRead: () => void;
	toggleFavorite: (id: string) => void;
	removeNotification: (id: string) => void;
	clearAll: () => void;
	unreadCount: () => number;
	pruneOld: () => void;
}

const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
const MAX_NOTIFICATIONS = 50;

export const useNotificationStore = create<NotificationStore>()(
	persist(
		(set, get) => ({
			notifications: [],

			addNotification: (n) => {
				set((state) => ({
					notifications: [
						{ ...n, read: false, favorited: false },
						...state.notifications,
					].slice(0, MAX_NOTIFICATIONS),
				}));
			},

			updateNotification: (id, updates) => {
				set((state) => ({
					notifications: state.notifications.map((n) =>
						n.id === id ? { ...n, ...updates } : n,
					),
				}));
			},

			markRead: (id) => {
				set((state) => ({
					notifications: state.notifications.map((n) =>
						n.id === id ? { ...n, read: true } : n,
					),
				}));
			},

			markAllRead: () => {
				set((state) => ({
					notifications: state.notifications.map((n) => ({
						...n,
						read: true,
					})),
				}));
			},

			toggleFavorite: (id) => {
				set((state) => ({
					notifications: state.notifications.map((n) =>
						n.id === id ? { ...n, favorited: !n.favorited } : n,
					),
				}));
			},

			removeNotification: (id) => {
				set((state) => ({
					notifications: state.notifications.filter((n) => n.id !== id),
				}));
			},

			clearAll: () => {
				set({ notifications: [] });
			},

			unreadCount: () => {
				return get().notifications.filter((n) => !n.read).length;
			},

			pruneOld: () => {
				const cutoff = Date.now() - TWENTY_FOUR_HOURS;
				set((state) => ({
					notifications: state.notifications.filter(
						(n) => new Date(n.createdAt).getTime() > cutoff,
					),
				}));
			},
		}),
		{
			name: "notification-storage",
			onRehydrateStorage: () => (state) => {
				// Auto-prune on hydration
				if (state) {
					state.pruneOld();
				}
			},
		},
	),
);
